import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createNodeSelection,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isNodeSelection,
  $isParagraphNode,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COPY_COMMAND,
  createCommand,
  CUT_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_BACKSPACE_COMMAND,
  PASTE_COMMAND,
  ParagraphNode,
  RootNode,
  type ElementNode,
  type LexicalCommand,
  type LexicalNode,
} from "lexical";
import { useEffect } from "react";

import {
  $createMergeTagNode,
  $isMergeTagNode,
  type MergeTagNode,
} from "./MergeTagNode";
import {
  $createProductBlockNode,
  $isProductBlockNode,
  ProductBlockNode,
} from "./ProductBlockNode";
import {
  $createProductContextNode,
  $isProductContextNode,
  productModelLabel,
  ProductContextNode,
} from "./ProductContextNode";
import { getMergeTagByIdentifier } from "../mergeTagDefinitions";
import {
  isProductBlockClose,
  parseProductBlockOpen,
  PRODUCT_BLOCK_CLOSE_MARKER,
  productBlockOpenMarker,
} from "../productBlocks";
import {
  DISCOVER_HEADING_TAG,
  isProductContextClose,
  parseProductContextOpen,
  PRODUCT_CONTEXT_CLOSE_MARKER,
  productContextOpenMarker,
} from "../productContexts";
import {
  maxProductItemIndex,
  parseProductItemIdentifier,
  productItemIdentifier,
  productItemLabel,
} from "../productItemTags";

/** Appends the next product block to the caret's context (or after the caret). */
export const INSERT_PRODUCT_BLOCK_COMMAND: LexicalCommand<void> = createCommand(
  "INSERT_PRODUCT_BLOCK_COMMAND",
);

/** Payload is the product model the new context reads. */
export const INSERT_PRODUCT_CONTEXT_COMMAND: LexicalCommand<string> =
  createCommand("INSERT_PRODUCT_CONTEXT_COMMAND");

/** Nearest product block wrapping `node`, if any. */
export function $ancestorProductBlock(
  node: LexicalNode | null,
): ProductBlockNode | null {
  while (node) {
    if ($isProductBlockNode(node)) return node;
    node = node.getParent();
  }
  return null;
}

/** Nearest product context wrapping `node`, if any. */
export function $ancestorProductContext(
  node: LexicalNode | null,
): ProductContextNode | null {
  while (node) {
    if ($isProductContextNode(node)) return node;
    node = node.getParent();
  }
  return null;
}

function $selectionAnchorNode(): LexicalNode | null {
  const selection = $getSelection();
  return $isRangeSelection(selection) ? selection.anchor.getNode() : null;
}

/** True when the selection point sits at the very start of `paragraph`. */
function $isAtStartOfLine(
  point: { offset: number; getNode: () => LexicalNode },
  paragraph: ParagraphNode,
): boolean {
  if (point.offset !== 0) return false;
  const node = point.getNode();
  return node.is(paragraph) || node.is(paragraph.getFirstDescendant());
}

/** The product context the selection sits in, if any. */
export function $selectedProductContext(): ProductContextNode | null {
  return $ancestorProductContext($selectionAnchorNode());
}

/** The product block the selection sits in, if any. */
export function $selectedProductBlock(): ProductBlockNode | null {
  return $ancestorProductBlock($selectionAnchorNode());
}

/** The message's discover section — there is at most one authored group. */
export function $findDiscoverContext(): ProductContextNode | null {
  return (
    $getRoot()
      .getChildren()
      .find(
        (child): child is ProductContextNode =>
          $isProductContextNode(child) && child.getModel() === "discover",
      ) ?? null
  );
}

function $isHeadingChip(node: LexicalNode): node is MergeTagNode {
  return $isMergeTagNode(node) && node.getTag() === DISCOVER_HEADING_TAG;
}

/** A line carrying the Discover headline chip — it repeats per set, like a block. */
export function $isDiscoverHeadingLine(node: LexicalNode): boolean {
  return $isParagraphNode(node) && node.getChildren().some($isHeadingChip);
}

/** Every Discover headline chip under `element`, however deeply nested. */
function $headingChipsIn(element: ElementNode): MergeTagNode[] {
  const chips: MergeTagNode[] = [];
  for (const child of element.getChildren()) {
    if ($isHeadingChip(child)) chips.push(child);
    else if ($isElementNode(child)) chips.push(...$headingChipsIn(child));
  }
  return chips;
}

/** True when `context` carries the Discover headline anywhere inside it. */
export function $hasDiscoverHeadingChip(context: ProductContextNode): boolean {
  return $headingChipsIn(context).length > 0;
}

/**
 * Drops the Discover headline chips from `context`. The tag reads the discover
 * section's own `heading=` binding, so under any other model it resolves to
 * nothing — the switch takes the chip with it. Surrounding words stay; a line
 * the chip leaves empty goes too, unless it is all its parent has left.
 */
export function $stripDiscoverHeadingChips(context: ProductContextNode): void {
  for (const chip of $headingChipsIn(context)) {
    const line = chip.getParent();
    // preserveEmptyParent — the now-empty line is dropped below, not here,
    // so a chip-only line can't cascade its block away with it.
    chip.remove(true);
    if (!$isElementNode(line) || line.getTextContentSize() > 0) continue;
    const parent = line.getParent();
    if ($isElementNode(parent) && parent.getChildrenSize() > 1) line.remove();
  }
}

/**
 * Folds a second discover section into the one already in the document. There
 * is a single authored Discover group per message — sets 2–5 are display
 * chrome, and the wire transform is what fans the authored set out across the
 * five bindings — so a pasted or loaded duplicate contributes its lines rather
 * than standing up its own section.
 *
 * The lines land at `marker` when it was pasted inside the group (the caret's
 * own line, or the block holding it — blocks never nest), and at the end of
 * the group otherwise.
 */
function $foldIntoDiscoverContext(
  existing: ProductContextNode,
  marker: LexicalNode,
  lines: LexicalNode[],
): void {
  // Climb to `marker`'s own line within the group, if it is in there at all.
  let anchor: LexicalNode | null = marker;
  while (anchor && !existing.is(anchor.getParent()))
    anchor = anchor.getParent();

  for (const line of lines) {
    if (anchor) {
      anchor.insertAfter(line);
      anchor = line;
    } else {
      existing.append(line);
    }
    // Re-parenting dirties the line, not its text: re-run text transforms so
    // context-scoped tags (the Discover headline) chip inside their new host.
    if ($isElementNode(line)) {
      for (const text of line.getAllTextNodes()) text.markDirty();
    }
  }
  marker.remove();
}

/**
 * Items `context` claims from its model: 0 for an empty wrapper (no blocks,
 * no product tags — plain copy only), else at least one. Mirrors
 * `productContextLayout` in the string-level library.
 */
function $contextClaimSpan(context: ProductContextNode, base: number): number {
  let maxRef = -1;
  let hasBlocks = false;
  for (const inner of context.getChildren()) {
    if ($isProductBlockNode(inner)) {
      hasBlocks = true;
      maxRef = Math.max(maxRef, inner.getIndex());
    } else if ($isElementNode(inner)) {
      maxRef = Math.max(maxRef, maxProductItemIndex(inner.getTextContent()));
    }
  }
  if (!hasBlocks && maxRef < 0) return 0;
  return Math.max(1, maxRef - base + 1);
}

/**
 * First item index `target` fills — the per-model running sum over the
 * contexts before it, the same rule the backend applies when it sums
 * `max_products` per model.
 */
export function $productContextBase(target: ProductContextNode): number {
  // Discover indices restart at 0 in every grid.
  if (target.getModel() === "discover") return 0;
  const running: Record<string, number> = {};
  for (const child of $getRoot().getChildren()) {
    if (!$isProductContextNode(child)) continue;
    if (child.getModel() === "discover") continue;
    const base = running[child.getModel()] ?? 0;
    if (child.is(target)) return base;
    running[child.getModel()] = base + $contextClaimSpan(child, base);
  }
  return 0;
}

/**
 * Deletes a block or context outright. A context losing its last block stays
 * alive holding one empty line — sections die only by explicit deletion — and
 * the caret lands somewhere sensible.
 */
export function $deleteProductWrapper(
  wrapper: ProductBlockNode | ProductContextNode,
): void {
  const parent = wrapper.getParent();
  const previous = wrapper.getPreviousSibling();
  // preserveEmptyParent — a context losing its last block must not cascade
  // away with it; the placeholder below keeps it alive instead.
  wrapper.remove(true);

  if ($isProductContextNode(parent) && parent.getChildrenSize() === 0) {
    const placeholder = $createParagraphNode();
    parent.append(placeholder);
    placeholder.select();
    return;
  }
  const root = $getRoot();
  if (root.getChildrenSize() === 0) {
    const placeholder = $createParagraphNode();
    root.append(placeholder);
    placeholder.select();
    return;
  }
  if (previous) previous.selectEnd();
  else if ($isElementNode(parent)) parent.selectStart();
}

function $seedChip(
  index: number,
  field: string,
  fieldLabel: string,
): MergeTagNode {
  const identifier = productItemIdentifier(index, field);
  const entry = getMergeTagByIdentifier(identifier);
  return $createMergeTagNode(
    identifier,
    entry?.label ?? productItemLabel(index, fieldLabel),
  );
}

/**
 * A block always holds at least one paragraph, seeded with the product's name
 * and link chips (`{{ name }} - {{ item_url }}`). The index is provisional —
 * `$reindexProductBlocks` settles it (and the chips) in the same update cycle.
 */
function $seedParagraph(index: number): ParagraphNode {
  const paragraph = $createParagraphNode();
  paragraph.append(
    $seedChip(index, "name", "Name"),
    $createTextNode(" - "),
    $seedChip(index, "item_url", "Link"),
  );
  return paragraph;
}

/**
 * Inserts a fresh context (with one block) after the caret's line — the shared
 * path for the toolbar button and for product-tag inserts outside any context.
 * The block's chip is `seedChip` when given, else the product's name.
 */
export function $insertProductContextAtCaret(
  model: string,
  seedChip?: MergeTagNode,
): void {
  // One discover group per message: a second insert grows the existing one.
  if (model === "discover") {
    const existing = $findDiscoverContext();
    if (existing) {
      $insertProductBlockInContext(existing, null, seedChip);
      return;
    }
  }
  const context = $createProductContextNode(model);
  const block = $createProductBlockNode(0);
  if (seedChip) {
    const paragraph = $createParagraphNode();
    paragraph.append(seedChip);
    block.append(paragraph);
  } else {
    block.append($seedParagraph(0));
  }
  context.append(block);

  const top = $selectionAnchorNode()?.getTopLevelElement() ?? null;
  if (top) top.insertAfter(context);
  else $getRoot().append(context);

  block.selectEnd();
}

/**
 * Wraps `seedChip` in a new product block at the caret's line inside `context`,
 * or seeds the standard name + link paragraph when there is no chip to carry —
 * the in-context mirror of `$insertProductContextAtCaret`, so a product tag
 * added while inside a wrapper lands guarded rather than as a loose (always-on)
 * chip. `line` is the caret's own line within the context; it's dropped when the
 * new block leaves it empty (the placeholder a removed block leaves behind). The
 * provisional index is settled by `$reindexProductBlocks` in the same cycle.
 */
export function $insertProductBlockInContext(
  context: ProductContextNode,
  line: LexicalNode | null,
  seedChip?: MergeTagNode,
): void {
  const block = $createProductBlockNode(0);
  if (seedChip) {
    const paragraph = $createParagraphNode();
    paragraph.append(seedChip);
    block.append(paragraph);
  } else {
    block.append($seedParagraph(0));
  }

  if (line !== null && context.is(line.getParent())) {
    line.insertAfter(block);
    if ($isElementNode(line) && line.getTextContentSize() === 0) line.remove();
  } else {
    context.append(block);
  }
  block.selectEnd();
}

/**
 * Repoints every product chip inside `element` at `index`, labelled for
 * `modelLabel` (the enclosing context's model) so the same index reads apart
 * across contexts. Relabels on a model change too, not just an index change —
 * the setter stays gated so the transform still converges.
 */
function $repointProductChips(
  element: ElementNode,
  index: number,
  modelLabel: string,
): void {
  for (const child of element.getChildren()) {
    if ($isMergeTagNode(child)) {
      const item = parseProductItemIdentifier(child.getTag());
      if (item === null) continue;
      const identifier = productItemIdentifier(index, item.field);
      const entry = getMergeTagByIdentifier(identifier, modelLabel);
      const label =
        entry?.label ?? productItemLabel(index, item.field, modelLabel);
      if (child.getTag() !== identifier || child.getLabel() !== label) {
        child.setTag(identifier, label);
      }
    } else if ($isElementNode(child)) {
      $repointProductChips(child, index, modelLabel);
    }
  }
}

/**
 * Keeps every block's absolute index honest: blocks are numbered positionally
 * per model across the whole document (`base = items already claimed by
 * earlier same-model contexts`), and the chips inside a block always read the
 * block's own product. This is the single mechanism — headers, chips and the
 * serialised markers all read the stored index this pass maintains.
 *
 * Loose chips in context copy (outside any block) keep their authored index
 * but still count toward the range the context claims.
 */
export function $reindexProductBlocks(root: RootNode): void {
  const running: Record<string, number> = {};
  let discoverOrdinal = 0;
  for (const child of root.getChildren()) {
    if (!$isProductContextNode(child)) continue;
    // Discover binds one items array per grid: the grid ordinal is positional
    // among discover sections, and indices restart at 0 inside each.
    const isDiscover = child.getModel() === "discover";
    if (isDiscover && child.getGrid() !== discoverOrdinal) {
      child.setGrid(discoverOrdinal);
    }
    if (isDiscover) discoverOrdinal += 1;
    const base = isDiscover ? 0 : (running[child.getModel()] ?? 0);
    const modelLabel = productModelLabel(child.getModel());
    let position = 0;
    for (const inner of child.getChildren()) {
      if (!$isProductBlockNode(inner)) continue;
      const index = base + position;
      position += 1;
      if (inner.getIndex() !== index) inner.setIndex(index);
      $repointProductChips(inner, index, modelLabel);
    }
    if (!isDiscover) {
      running[child.getModel()] = base + $contextClaimSpan(child, base);
    }
  }
}

/**
 * Unwraps a context into plain copy: blocks dissolve into their lines and
 * product chips are removed — an `items.N` chip is meaningless outside a
 * context, so "keep text" keeps the words, not the tags.
 */
export function $unwrapContextKeepingCopy(context: ProductContextNode): void {
  const lines: LexicalNode[] = [];
  const flatten = (element: ElementNode): void => {
    for (const child of element.getChildren()) {
      if ($isProductBlockNode(child)) flatten(child);
      else lines.push(child);
    }
  };
  flatten(context);

  for (const line of lines) {
    if (!$isElementNode(line)) continue;
    for (const child of line.getChildren()) {
      if (
        $isMergeTagNode(child) &&
        (parseProductItemIdentifier(child.getTag()) !== null ||
          $isHeadingChip(child))
      ) {
        // preserveEmptyParent — a chip-only line stays as an (empty) line.
        child.remove(true);
      }
    }
  }

  let anchor: LexicalNode = context;
  for (const line of lines) {
    anchor.insertAfter(line);
    anchor = line;
  }
  context.remove();
}

/**
 * Serialised authoring text for the parts of the document the selection
 * touches. The default clipboard text comes from the selection's text nodes
 * and never sees the wrapper nodes' `getTextContent`, so a copied product
 * section would lose its `{% with %}` / `{% if %}` markers — and with them the
 * model. Wrappers the selection touches contribute their markers here, and
 * lines are contributed whole (line-granular inside product sections).
 * Returns null when no wrapper is involved — default copy behavior applies.
 */
function $selectionTextWithProductMarkers(): string | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || selection.isCollapsed()) return null;

  const touched = new Set<string>();
  let touchesWrapper = false;
  for (const node of selection.getNodes()) {
    let current: LexicalNode | null = node;
    while (current) {
      touched.add(current.getKey());
      if ($isProductBlockNode(current) || $isProductContextNode(current)) {
        touchesWrapper = true;
      }
      current = current.getParent();
    }
  }
  if (!touchesWrapper) return null;

  const serialize = (node: LexicalNode): string => {
    if ($isProductContextNode(node)) {
      const inner = node
        .getChildren()
        .filter((child) => touched.has(child.getKey()))
        .map(serialize)
        .join("\n");
      return [
        productContextOpenMarker(node.getModel(), node.getGrid()),
        inner,
        PRODUCT_CONTEXT_CLOSE_MARKER,
      ].join("\n");
    }
    if ($isProductBlockNode(node)) {
      const inner = node
        .getChildren()
        .filter((child) => touched.has(child.getKey()))
        .map(serialize)
        .join("\n");
      return [
        productBlockOpenMarker(node.getIndex()),
        inner,
        PRODUCT_BLOCK_CLOSE_MARKER,
      ].join("\n");
    }
    return node.getTextContent();
  };

  return $getRoot()
    .getChildren()
    .filter((child) => touched.has(child.getKey()))
    .map(serialize)
    .join("\n");
}

/**
 * Rebuilds product contexts and blocks from the `{% with %}` / `{% if items.N %}`
 * markers that `bodyText` round-trips through, keeps their indices honest, and
 * handles explicit insertion.
 *
 * Because contexts and blocks own whole lines, reconstruction is a sibling
 * scan — no offsets into the flat string, which is what made an inline range
 * wrapper impractical here.
 */
export function ProductBlockPlugin(): null {
  const [editor] = useLexicalComposerContext();

  // One paragraph per line: the plain-text editor pastes (and shift-enters)
  // multi-line text as LineBreakNodes inside a single paragraph, but the
  // marker transforms below match whole-line paragraphs — a pasted
  // `{% with %}` section would stay inert plain text. Splitting here makes
  // paste, typing and the load path all converge on the same shape.
  useEffect(() => {
    return editor.registerNodeTransform(ParagraphNode, (paragraph) => {
      const children = paragraph.getChildren();
      if (!children.some($isLineBreakNode)) return;

      const lines: LexicalNode[][] = [[]];
      for (const child of children) {
        if ($isLineBreakNode(child)) {
          child.remove();
          lines.push([]);
        } else {
          lines[lines.length - 1].push(child);
        }
      }

      // The first line keeps the original paragraph; later lines move into
      // fresh siblings (append re-parents the nodes, so a selection inside
      // them survives the split).
      let anchor: ParagraphNode = paragraph;
      for (const lineChildren of lines.slice(1)) {
        const next = $createParagraphNode();
        next.append(...lineChildren);
        anchor.insertAfter(next);
        anchor = next;
      }
    });
  }, [editor]);

  // Contexts rebuild first; the block transform below then runs on the marker
  // paragraphs now sitting inside them, so nesting falls out of transform order
  // rather than needing a second parser.
  useEffect(() => {
    return editor.registerNodeTransform(ParagraphNode, (node) => {
      const open = parseProductContextOpen(node.getTextContent());
      if (open === null) return;

      const inner: LexicalNode[] = [];
      let sibling = node.getNextSibling();
      let closer: LexicalNode | null = null;

      while (sibling) {
        if (isProductContextClose(sibling.getTextContent())) {
          closer = sibling;
          break;
        }
        inner.push(sibling);
        sibling = sibling.getNextSibling();
      }

      // One discover group per message, however the markers arrived: a second
      // set folds into the first instead of becoming its own section.
      const group = open.model === "discover" ? $findDiscoverContext() : null;
      if (group) {
        closer?.remove();
        $foldIntoDiscoverContext(group, node, inner);
        return;
      }

      const context = $createProductContextNode(open.model, open.grid ?? 0);
      for (const child of inner) {
        context.append(child);
        // Re-parenting dirties the line, not its text: re-run text transforms
        // so context-scoped tags (the Discover headline) chip once inside.
        if ($isElementNode(child)) {
          for (const text of child.getAllTextNodes()) text.markDirty();
        }
      }
      closer?.remove();

      if (context.getChildrenSize() === 0)
        context.append($createParagraphNode());

      // Contexts never nest: markers pasted inside a section hoist the new
      // context out, as a sibling below the enclosing one.
      const host =
        $ancestorProductContext(node.getParent()) ??
        $ancestorProductBlock(node.getParent());
      if (host) {
        node.remove();
        host.insertAfter(context);
      } else {
        node.replace(context);
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerNodeTransform(ParagraphNode, (node) => {
      const index = parseProductBlockOpen(node.getTextContent());
      if (index === null) return;

      // A pending context-open marker before this block owns everything up to
      // its endwith — defer so the context transform consumes the region first
      // (transform order across dirty nodes isn't guaranteed), then rebuild
      // inside the new context.
      for (
        let before = node.getPreviousSibling();
        before;
        before = before.getPreviousSibling()
      ) {
        if (!$isParagraphNode(before)) continue;
        const text = before.getTextContent();
        if (parseProductContextOpen(text) !== null) return;
        if (isProductContextClose(text)) break;
      }

      const inner: ParagraphNode[] = [];
      let sibling = node.getNextSibling();
      let closer: LexicalNode | null = null;

      while (sibling) {
        if (isProductBlockClose(sibling.getTextContent())) {
          closer = sibling;
          break;
        }
        if (sibling instanceof ParagraphNode) inner.push(sibling);
        sibling = sibling.getNextSibling();
      }

      const block = $createProductBlockNode(index);
      // Paragraphs move in whole — the block is a container, so each line
      // inside stays its own node.
      for (const paragraph of inner) block.append(paragraph);
      closer?.remove();

      if (block.getChildrenSize() === 0) block.append($seedParagraph(index));

      // Blocks never nest: markers pasted inside a block hoist the new block
      // out, as a sibling below the enclosing one (still inside the context).
      const host = $ancestorProductBlock(node.getParent());
      if (host) {
        node.remove();
        host.insertAfter(block);
      } else {
        node.replace(block);
      }
    });
  }, [editor]);

  // A block whose children are inline (hand-built or pasted) can't host a
  // caret line break — Lexical needs a block ancestor for the inline content.
  useEffect(() => {
    return editor.registerNodeTransform(ProductBlockNode, (block) => {
      const children = block.getChildren();
      const stray = children.filter((child) => !$isParagraphNode(child));
      if (stray.length === 0) return;
      const paragraph = $createParagraphNode();
      for (const child of stray) paragraph.append(child);
      if (children[0] === stray[0]) {
        block.splice(0, stray.length, [paragraph]);
      } else {
        block.append(paragraph);
      }
    });
  }, [editor]);

  // Root transforms run whenever anything in the document changes (every
  // ancestor of a dirty node is a dirty element), so deleting a block or a
  // whole context renumbers everything after it in the same update. The
  // change-gated setters keep the pass convergent.
  useEffect(() => {
    return editor.registerNodeTransform(RootNode, $reindexProductBlocks);
  }, [editor]);

  // Copy keeps the product structure (see $selectionTextWithProductMarkers).
  // Only text/plain is written, so pasting back re-parses the markers instead
  // of restoring possibly-partial cloned nodes.
  useEffect(() => {
    const handleClipboard = (event: unknown, cut: boolean): boolean => {
      const clipboard =
        event !== null && typeof event === "object" && "clipboardData" in event
          ? (event as ClipboardEvent)
          : null;
      if (!clipboard?.clipboardData) return false;
      const text = $selectionTextWithProductMarkers();
      if (text === null) return false;
      clipboard.preventDefault();
      clipboard.clipboardData.setData("text/plain", text);
      if (cut) {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) selection.removeText();
      }
      return true;
    };
    const unregisterCopy = editor.registerCommand(
      COPY_COMMAND,
      (event) => handleClipboard(event, false),
      COMMAND_PRIORITY_HIGH,
    );
    const unregisterCut = editor.registerCommand(
      CUT_COMMAND,
      (event) => handleClipboard(event, true),
      COMMAND_PRIORITY_HIGH,
    );
    return () => {
      unregisterCopy();
      unregisterCut();
    };
  }, [editor]);

  /**
   * Structural paste: content carrying marker lines is inserted as whole
   * lines, the caret's line split around it. The default paste merges the
   * first pasted line into the caret's line, which corrupts a leading
   * `{% with %}` marker — the section's model silently degrades to inert text
   * while its inner blocks still activate.
   */
  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboard =
          event !== null &&
          typeof event === "object" &&
          "clipboardData" in event
            ? event
            : null;
        const text = clipboard?.clipboardData?.getData("text/plain");
        if (!text) return false;
        const lines = text.split("\n");
        const hasMarker = lines.some(
          (line) =>
            parseProductContextOpen(line) !== null ||
            isProductContextClose(line) ||
            parseProductBlockOpen(line) !== null ||
            isProductBlockClose(line),
        );
        if (!hasMarker) return false;
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;
        clipboard.preventDefault();

        if (!selection.isCollapsed()) selection.removeText();
        // Split the caret's line; the pasted lines go between the halves.
        selection.insertParagraph();
        let tail: LexicalNode | null = $selectionAnchorNode();
        while (tail && !$isParagraphNode(tail)) tail = tail.getParent();
        if (!$isParagraphNode(tail)) return true;
        const head = tail.getPreviousSibling();

        const paragraphs = lines.map((line) => {
          const paragraph = $createParagraphNode();
          if (line.length > 0) paragraph.append($createTextNode(line));
          return paragraph;
        });
        for (const paragraph of paragraphs) tail.insertBefore(paragraph);

        // A caret that sat at a line edge leaves an empty half — drop it.
        if ($isParagraphNode(head) && head.getTextContentSize() === 0) {
          head.remove(true);
        }
        if (tail.getTextContentSize() === 0) tail.remove(true);
        paragraphs[paragraphs.length - 1]?.selectEnd();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  /**
   * Backspace never dissolves structure. At a structural boundary — the start
   * of a wrapper's first line — it selects the wrapper (the visible "are you
   * sure"), and a second Backspace deletes the whole thing. There is no
   * keystroke path that unwraps a section and orphans its product chips.
   */
  useEffect(() => {
    return editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event) => {
        const selection = $getSelection();

        if ($isNodeSelection(selection)) {
          const wrappers = selection
            .getNodes()
            .filter(
              (node): node is ProductBlockNode | ProductContextNode =>
                $isProductBlockNode(node) || $isProductContextNode(node),
            );
          if (wrappers.length === 0) return false;
          event?.preventDefault();
          for (const wrapper of wrappers) $deleteProductWrapper(wrapper);
          return true;
        }

        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }
        let paragraph: LexicalNode | null = selection.anchor.getNode();
        while (paragraph && !$isParagraphNode(paragraph)) {
          paragraph = paragraph.getParent();
        }
        if (!$isParagraphNode(paragraph)) return false;
        if (paragraph.getPreviousSibling() !== null) return false;
        if (!$isAtStartOfLine(selection.anchor, paragraph)) return false;

        const parent = paragraph.getParent();
        if (!$isProductBlockNode(parent) && !$isProductContextNode(parent)) {
          return false;
        }

        event?.preventDefault();
        const nodeSelection = $createNodeSelection();
        nodeSelection.add(parent.getKey());
        $setSelection(nodeSelection);
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  // Visible highlight for the two-step delete: wrappers in a node selection
  // carry a class the editor styles.
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        const selected = $isNodeSelection(selection)
          ? new Set(selection.getNodes().map((node) => node.getKey()))
          : null;
        for (const child of $getRoot().getChildren()) {
          const nodes = $isProductContextNode(child)
            ? [child, ...child.getChildren().filter($isProductBlockNode)]
            : $isProductBlockNode(child)
              ? [child]
              : [];
          for (const node of nodes) {
            editor
              .getElementByKey(node.getKey())
              ?.classList.toggle(
                "lexical-structure-selected",
                selected?.has(node.getKey()) ?? false,
              );
          }
        }
      });
    });
  }, [editor]);

  /**
   * Enter on an empty line inside a wrapper pops out one level, the way nested
   * lists behave — block → context → root, so there's always a keyboard way
   * out however deep the caret sits. Enter at the very start of a block's
   * first line opens a line above the block (a headline slot), the Docs
   * gesture — the caret stays with the content. Any other Enter falls through
   * to the rich-text default (split the paragraph in place).
   */
  useEffect(() => {
    return editor.registerCommand(
      INSERT_PARAGRAPH_COMMAND,
      () => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }
        // Not getTopLevelElement — that returns the outermost wrapper, while
        // the caret's own line is a paragraph nested inside it.
        let paragraph: LexicalNode | null = selection.anchor.getNode();
        while (paragraph && !$isParagraphNode(paragraph)) {
          paragraph = paragraph.getParent();
        }
        if (!$isParagraphNode(paragraph)) return false;

        const parent = paragraph.getParent();
        if (!$isProductBlockNode(parent) && !$isProductContextNode(parent)) {
          return false;
        }

        if (paragraph.getTextContent().length > 0) {
          if (
            $isProductBlockNode(parent) &&
            paragraph.getPreviousSibling() === null &&
            $isAtStartOfLine(selection.anchor, paragraph)
          ) {
            parent.insertBefore($createParagraphNode());
            return true;
          }
          return false;
        }

        // Move the empty line out past the wrapper. Wrappers can't be empty,
        // so a lone child is left behind and a fresh paragraph is used instead.
        if (parent.getChildrenSize() > 1) {
          parent.insertAfter(paragraph);
          paragraph.select();
        } else {
          const after = $createParagraphNode();
          parent.insertAfter(after);
          after.select();
        }
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      INSERT_PRODUCT_BLOCK_COMMAND,
      () => {
        // Provisional index 0 — the reindex pass assigns the real one.
        const block = $createProductBlockNode(0);
        block.append($seedParagraph(0));

        // Inside a context the block joins that section; otherwise it lands
        // after the caret's line at the top level.
        const context = $selectedProductContext();
        if (context) {
          context.append(block);
        } else {
          const top = $selectionAnchorNode()?.getTopLevelElement() ?? null;
          if (top) top.insertAfter(block);
          else $getRoot().append(block);
        }
        block.selectEnd();
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      INSERT_PRODUCT_CONTEXT_COMMAND,
      (model) => {
        $insertProductContextAtCaret(model);
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  return null;
}
